import React, { memo, useMemo } from 'react';
import { Tabs, Text } from '@Sage/hoi-poi-ui';
import { useNavigate, useParams, Route, Routes, Navigate } from 'react-router-dom';

import DetailLayout from 'containers/components/layouts/ContentDetailLayout';
import { getLiteral } from 'utils/getLiteral';

import ContentNavigator from 'containers/campaigns/marketing/ContentNavigator';
import SenderSettingsHeader from './senders/SenderSettingsHeader';
import DomainSettingsHeader from './domains/DomainSettingsHeader';
import SettingsSenders from './senders';
import SettingsDomains from './domains';

import './styles.scss';

const TABS = {
    senders: 'senders',
    domains: 'domains',
};

const MarketingSettings = memo(() => {
    const { '*': slug } = useParams();
    const navigate = useNavigate();

    const tabs = useMemo(
        () => [
            {
                key: TABS.senders,
                title: getLiteral('label_marketing_campaigns_senders'),
            },
            {
                key: TABS.domains,
                title: getLiteral('label_marketing_campaigns_domain_verification'),
            },
        ],
        [],
    );

    const activeTab = useMemo(() => {
        const matchedTab = Object.values(TABS).find((tab) => slug?.includes(tab));
        return matchedTab || TABS.senders;
    }, [slug]);

    const handleTabChange = (key) => {
        navigate(`/campaigns/marketing/settings/${key}`);
    };

    const header = useMemo(() => {
        const emptyHeader = <div className="marketing-settings__header" />;
        const headerMap = {
            [TABS.senders]: () => <SenderSettingsHeader />,
            [TABS.domains]: () => <DomainSettingsHeader />,
        };

        return headerMap[activeTab]?.() || emptyHeader;
    }, [activeTab]);

    return (
        <DetailLayout header={header} navigator={<ContentNavigator />}>
            <div className="marketing-settings__main">
                <div className="marketing-settings__main__header">
                    <Text type="h5" bold>
                        {getLiteral('label_settings')}
                    </Text>
                </div>
                <Tabs activeKey={activeTab} onChange={handleTabChange} tabs={tabs} />
                <Routes>
                    <Route path={`/${TABS.senders}`} element={<SettingsSenders />} />
                    <Route path={`/${TABS.domains}`} element={<SettingsDomains />} />
                    <Route path="*" element={<Navigate to={TABS.senders} replace />} />
                </Routes>
            </div>
        </DetailLayout>
    );
});

MarketingSettings.displayName = 'MarketingSettings';

export default MarketingSettings;
